import React, {
  FC,
  FormEvent,
  KeyboardEvent,
  MouseEvent as ReactMouseEvent,
  ReactElement,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import fileDownload from 'js-file-download';
import { useSelector } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { getUserTimeZone, IconMessageFill } from '@infologistics/frontend-libraries';

import {
  cancelAiAssistantRun,
  deleteAiAssistantThread,
  downloadAiAssistantFile,
  fetchAiAssistantThreadMessages,
  fetchAiAssistantThreads,
  getAiAssistantDownloadUrl,
  streamAiAssistantChat,
} from '@services/aiAssistant';
import { IAiAssistantHistoryMessage, IAiAssistantStreamEvent, IAiAssistantThread } from '@services/aiAssistant/types';
import { IApplicationState } from '@store/types/commonTypes';

import {
  IAiAssistantDownloadPart,
  IAiAssistantMessage,
  IAiAssistantRunProgress,
  IAiAssistantSuggestedAction,
  IAiAssistantTablePart,
} from './types';

import styles from './AiAssistant.module.css';
import { useTranslation } from 'react-i18next';

const EMPTY_STATUS = '';
const ASSISTANT_FALLBACK_MESSAGE_ID = 'assistant-current';
const URL_REGEXP = /(https?:\/\/[^\s<]+)/g;
const PANEL_ANIMATION_DURATION = 260;
const PANEL_WIDTH_STORAGE_KEY = 'aiAssistantPanelWidth';
const PANEL_DEFAULT_WIDTH = 420;
const PANEL_MIN_WIDTH = 360;
const PANEL_MAX_WIDTH = 1180;
const DOCUMENT_ID_COLUMN_KEYS = ['document_id', 'documentId', 'documentID', 'documentOguid', 'document_oguid'] as const;
const DOCUMENT_ID_COLUMN_LABELS = ['document id', 'document oguid', 'document guid'] as const;
const INTERRUPTED_THREAD_STORAGE_KEY = 'aiAssistantInterruptedThreadId';
const INTERRUPTED_THREAD_POLL_INTERVAL = 3000;
const INTERRUPTED_THREAD_MAX_POLL_ATTEMPTS = 5;
const SUPPORTED_TOOL_ACTIONS = ['fetch_next_page', 'export_query_result'] as const;
const HIDDEN_TABLE_COLUMN_KEYS = ['rowAction', 'action'] as const;
const OPEN_DOCUMENT_CARD_ACTION_TYPE = 'openDocumentCard';

type AiAssistantToolName = (typeof SUPPORTED_TOOL_ACTIONS)[number];

interface IAiAssistantToolCallRequest {
  toolName: AiAssistantToolName;
  toolArguments: Record<string, unknown>;
}

interface IAiAssistantActiveRequest {
  token: string;
  controller: AbortController;
  runId: string | null;
  threadId: string | null;
  threadTitle: string;
  messageId: string | null;
  progressClosed: boolean;
  progress: IAiAssistantRunProgress | null;
  responseStarted: boolean;
  userMessage: IAiAssistantMessage;
  assistantMessage: IAiAssistantMessage | null;
  suggestedActions: IAiAssistantSuggestedAction[];
  cancellationRequested: boolean;
  cancellationRequestSent: boolean;
  finished: boolean;
}

type AiAssistantThreadActivity = 'running' | 'completed';

type IAiAssistantSendRequest =
  | {
      displayText: string;
      text: string;
      toolCall?: never;
    }
  | {
      displayText: string;
      text?: never;
      toolCall: IAiAssistantToolCallRequest;
    };

function getInitialPanelWidth(): number {
  if (typeof window === 'undefined') return PANEL_DEFAULT_WIDTH;

  try {
    const storedWidth = Number(window.sessionStorage.getItem(PANEL_WIDTH_STORAGE_KEY));

    return Number.isFinite(storedWidth) && storedWidth > 0 ? clampPanelWidth(storedWidth) : clampPanelWidth(PANEL_DEFAULT_WIDTH);
  } catch {
    return clampPanelWidth(PANEL_DEFAULT_WIDTH);
  }
}

function getPanelWidthFromClientX(clientX: number): number {
  if (typeof window === 'undefined') return PANEL_DEFAULT_WIDTH;

  return clampPanelWidth(window.innerWidth - clientX);
}

function clampPanelWidth(width: number): number {
  if (typeof window === 'undefined') return Math.min(Math.max(width, PANEL_MIN_WIDTH), PANEL_MAX_WIDTH);

  const maxWidth = Math.min(PANEL_MAX_WIDTH, window.innerWidth);
  const minWidth = Math.min(PANEL_MIN_WIDTH, maxWidth);

  return Math.min(Math.max(width, minWidth), maxWidth);
}

function savePanelWidth(width: number): void {
  if (typeof window === 'undefined') return;

  try {
    window.sessionStorage.setItem(PANEL_WIDTH_STORAGE_KEY, String(width));
  } catch {
    // Ignore storage access restrictions.
  }
}

function getInterruptedThreadStorageKey(orgOguid: string, userOguid: string): string {
  return `${INTERRUPTED_THREAD_STORAGE_KEY}:${orgOguid}:${userOguid}`;
}

function readInterruptedThreadId(key: string): string | null {
  if (typeof window === 'undefined') return null;

  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeInterruptedThreadId(key: string, threadId: string): void {
  if (typeof window === 'undefined') return;

  try {
    window.sessionStorage.setItem(key, threadId);
  } catch {
    // Ignore storage access restrictions.
  }
}

function removeInterruptedThreadId(key: string, threadId?: string | null): void {
  if (typeof window === 'undefined') return;

  try {
    if (threadId && window.sessionStorage.getItem(key) !== threadId) return;

    window.sessionStorage.removeItem(key);
  } catch {
    // Ignore storage access restrictions.
  }
}

const AiAssistant: FC<RouteComponentProps> = ({ history }): ReactElement => {
  const { orgAlias, orgOguid, userOguid } = useSelector((state: IApplicationState) => ({
    orgAlias: state.user.activeOrganization.alias,
    orgOguid: state.user.activeOrganization.oguid,
    userOguid: state.user.profile.oguid,
  }));
  const {
    i18n: { language },
    t,
  } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [isPanelRendered, setIsPanelRendered] = useState(false);
  const [draft, setDraft] = useState('');
  const [messages, setMessages] = useState<IAiAssistantMessage[]>([]);
  const [pending, setPending] = useState(false);
  const [runProgress, setRunProgress] = useState<IAiAssistantRunProgress | null>(null);
  const [isResponseStarted, setIsResponseStarted] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [status, setStatus] = useState(EMPTY_STATUS);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [threads, setThreads] = useState<IAiAssistantThread[]>([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [threadActivities, setThreadActivities] = useState<Record<string, AiAssistantThreadActivity>>({});
  const [historyLoading, setHistoryLoading] = useState(false);
  const [deletingThreadId, setDeletingThreadId] = useState<string | null>(null);
  const [recoveringThreadId, setRecoveringThreadId] = useState<string | null>(null);
  const [suggestedActions, setSuggestedActions] = useState<IAiAssistantSuggestedAction[]>([]);
  const [panelWidth, setPanelWidth] = useState(getInitialPanelWidth);
  const [isResizing, setIsResizing] = useState(false);

  const historyAbortRef = useRef<AbortController | null>(null);
  const closeTimerRef = useRef<number | null>(null);
  const messagesRef = useRef<HTMLDivElement | null>(null);
  const recoveryPollTimerRef = useRef<number | null>(null);
  const isOpenRef = useRef(false);
  const threadIdRef = useRef<string | null>(null);
  const foregroundRequestTokenRef = useRef<string | null>(null);
  const activeRequestsRef = useRef<Map<string, IAiAssistantActiveRequest>>(new Map());
  const assistantContextRef = useRef({ orgOguid, userOguid });
  const isRunActive = pending || !!runProgress;

  const getInterruptedThreadId = (): string | null => {
    if (!orgOguid || !userOguid) return null;

    return readInterruptedThreadId(getInterruptedThreadStorageKey(orgOguid, userOguid));
  };

  const clearInterruptedThreadId = (id?: string | null): void => {
    if (!orgOguid || !userOguid) return;

    removeInterruptedThreadId(getInterruptedThreadStorageKey(orgOguid, userOguid), id);
  };

  const clearRecoveryPollTimer = (): void => {
    if (!recoveryPollTimerRef.current) return;

    window.clearTimeout(recoveryPollTimerRef.current);
    recoveryPollTimerRef.current = null;
  };

  const getLatestRequestForThread = (id: string, includeFinished = true): IAiAssistantActiveRequest | null => {
    const requests = Array.from(activeRequestsRef.current.values()).reverse();

    return requests.find((request) => request.threadId === id && (includeFinished || !request.finished)) || null;
  };

  const isRequestVisible = (request: IAiAssistantActiveRequest): boolean =>
    request.threadId
      ? request.threadId === threadIdRef.current
      : request.token === foregroundRequestTokenRef.current;

  const updateThreadActivity = (id: string, activity: AiAssistantThreadActivity | null): void => {
    setThreadActivities((current) => {
      if (!activity) {
        if (!current[id]) return current;

        const next = { ...current };

        delete next[id];

        return next;
      }

      if (current[id] === activity) return current;

      return {
        ...current,
        [id]: activity,
      };
    });
  };

  const loadThreads = async (showLoader = true, signal?: AbortSignal): Promise<void> => {
    if (!orgOguid || !userOguid) return;

    if (showLoader) {
      setThreadsLoading(true);
    }

    try {
      const loadedThreads = await fetchAiAssistantThreads({
        orgOguid,
        userOguid,
        locale: language,
        signal,
      });

      setThreads(mergeRequestThreads(loadedThreads, activeRequestsRef.current));
    } finally {
      if (showLoader) {
        setThreadsLoading(false);
      }
    }
  };

  const handleHistoryError = (error: Error, fallback: string): void => {
    if (error.name === 'AbortError') return;

    setStatus(error.message || fallback);
  };

  const loadThreadMessages = async (id: string, signal: AbortSignal): Promise<boolean> => {
    const historyMessages = await fetchAiAssistantThreadMessages(id, {
      orgOguid,
      userOguid,
      locale: language,
      signal,
    });

    const restoredMessages = normalizeHistoryMessages(historyMessages);
    const cachedRequest = getLatestRequestForThread(id);
    const messagesWithCachedResponse = mergeCachedRequestMessages(restoredMessages, cachedRequest || null);

    if (threadIdRef.current === id) {
      setMessages(messagesWithCachedResponse);
      setSuggestedActions(cachedRequest?.suggestedActions.length ? cachedRequest.suggestedActions : getLatestSuggestedActions(historyMessages));
    }

    return hasAssistantResponseAfterLastUser(messagesWithCachedResponse);
  };

  const finishInterruptedRecovery = (id: string): void => {
    clearInterruptedThreadId(id);
    setRecoveringThreadId((currentId) => (currentId === id ? null : currentId));
    clearRecoveryPollTimer();
    loadThreads(false).catch(() => undefined);
  };

  const failInterruptedRecovery = (id: string, error?: Error): void => {
    clearInterruptedThreadId(id);
    setRecoveringThreadId((currentId) => (currentId === id ? null : currentId));
    clearRecoveryPollTimer();

    if (error) {
      handleHistoryError(error, t('common:aiAssistant.threadMessagesLoadError'));
    } else {
      setStatus(t('common:aiAssistant.threadMessagesLoadError'));
    }
  };

  const scheduleInterruptedThreadRefresh = (id: string, attempt = 1): void => {
    clearRecoveryPollTimer();

    if (!isOpenRef.current) return;

    recoveryPollTimerRef.current = window.setTimeout(() => {
      recoveryPollTimerRef.current = null;

      if (!isOpenRef.current) return;

      const currentThreadId = threadIdRef.current;

      if (currentThreadId && currentThreadId !== id) return;

      const controller = new AbortController();

      historyAbortRef.current?.abort();
      historyAbortRef.current = controller;

      loadThreadMessages(id, controller.signal)
        .then((hasRecoveredResponse) => {
          if (hasRecoveredResponse) {
            finishInterruptedRecovery(id);

            return;
          }

          if (attempt >= INTERRUPTED_THREAD_MAX_POLL_ATTEMPTS) {
            failInterruptedRecovery(id);

            return;
          }

          scheduleInterruptedThreadRefresh(id, attempt + 1);
        })
        .catch((error) => {
          if (error.name === 'AbortError' || !isOpenRef.current || threadIdRef.current !== id) return;

          if (attempt >= INTERRUPTED_THREAD_MAX_POLL_ATTEMPTS) {
            failInterruptedRecovery(id, error);

            return;
          }

          handleHistoryError(error, t('common:aiAssistant.threadMessagesLoadError'));
          scheduleInterruptedThreadRefresh(id, attempt + 1);
        })
        .finally(() => {
          if (historyAbortRef.current === controller) {
            historyAbortRef.current = null;
          }
        });
    }, INTERRUPTED_THREAD_POLL_INTERVAL);
  };

  useEffect(() => {
    const previousContext = assistantContextRef.current;

    if (previousContext.orgOguid === orgOguid && previousContext.userOguid === userOguid) return;

    const selectedRequest = threadIdRef.current ? getLatestRequestForThread(threadIdRef.current, false) : null;

    if (selectedRequest?.threadId && previousContext.orgOguid && previousContext.userOguid) {
      writeInterruptedThreadId(
        getInterruptedThreadStorageKey(previousContext.orgOguid, previousContext.userOguid),
        selectedRequest.threadId,
      );
    }

    activeRequestsRef.current.forEach((request) => {
      if (!request.finished) {
        request.finished = true;
        request.controller.abort();
      }
    });
    activeRequestsRef.current.clear();
    foregroundRequestTokenRef.current = null;
    historyAbortRef.current?.abort();
    historyAbortRef.current = null;
    clearRecoveryPollTimer();
    threadIdRef.current = null;
    assistantContextRef.current = { orgOguid, userOguid };

    setThreadId(null);
    setThreads([]);
    setThreadActivities({});
    setMessages([]);
    setSuggestedActions([]);
    setDraft('');
    setStatus(EMPTY_STATUS);
    setRunProgress(null);
    setIsResponseStarted(false);
    setIsCancelling(false);
    setPending(false);
    setThreadsLoading(false);
    setHistoryLoading(false);
    setDeletingThreadId(null);
    setRecoveringThreadId(null);
  }, [orgOguid, userOguid]);

  useEffect(
    () => () => {
      const selectedRequest = threadIdRef.current ? getLatestRequestForThread(threadIdRef.current, false) : null;
      const currentContext = assistantContextRef.current;

      if (selectedRequest?.threadId && currentContext.orgOguid && currentContext.userOguid) {
        writeInterruptedThreadId(
          getInterruptedThreadStorageKey(currentContext.orgOguid, currentContext.userOguid),
          selectedRequest.threadId,
        );
      }

      activeRequestsRef.current.forEach((request) => {
        if (!request.finished) {
          request.finished = true;
          request.controller.abort();
        }
      });
      historyAbortRef.current?.abort();
      activeRequestsRef.current.clear();
      foregroundRequestTokenRef.current = null;
      clearRecoveryPollTimer();

      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    threadIdRef.current = threadId;
  }, [threadId]);

  useEffect(() => {
    isOpenRef.current = isOpen;

    if (!isOpen) {
      clearRecoveryPollTimer();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isPanelRendered || !isOpen || !orgOguid || !userOguid) return;

    const controller = new AbortController();

    historyAbortRef.current?.abort();
    historyAbortRef.current = controller;

    loadThreads(true, controller.signal)
      .then(() => {
        const interruptedThreadId = getInterruptedThreadId();

        const currentThreadId = threadIdRef.current;

        if (!interruptedThreadId || (currentThreadId && currentThreadId !== interruptedThreadId)) return;

        threadIdRef.current = interruptedThreadId;
        setThreadId(interruptedThreadId);
        setHistoryLoading(true);
        setRecoveringThreadId(interruptedThreadId);

        return loadThreadMessages(interruptedThreadId, controller.signal)
          .then((hasRecoveredResponse) => {
            if (hasRecoveredResponse) {
              finishInterruptedRecovery(interruptedThreadId);

              return;
            }

            scheduleInterruptedThreadRefresh(interruptedThreadId);
          })
          .catch((error) => {
            if (error.name === 'AbortError') return;

            handleHistoryError(error, t('common:aiAssistant.threadMessagesLoadError'));
            scheduleInterruptedThreadRefresh(interruptedThreadId);
          });
      })
      .catch((error) => handleHistoryError(error, t('common:aiAssistant.historyLoadError')))
      .finally(() => {
        if (historyAbortRef.current === controller) {
          historyAbortRef.current = null;
        }

        setHistoryLoading(false);
      });

    return () => {
      controller.abort();
    };
  }, [isPanelRendered, isOpen, orgOguid, userOguid, language]);

  useEffect(() => {
    if (!messagesRef.current) return;

    messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
  }, [messages, suggestedActions, isOpen, pending, runProgress, isResponseStarted]);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (event: MouseEvent): void => {
      const nextWidth = getPanelWidthFromClientX(event.clientX);

      setPanelWidth(nextWidth);
      savePanelWidth(nextWidth);
    };

    const handleMouseUp = (): void => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.classList.add(styles.resizingBody);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.classList.remove(styles.resizingBody);
    };
  }, [isResizing]);

  const openChat = (): void => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }

    isOpenRef.current = true;
    setIsPanelRendered(true);
    setIsOpen(true);
    if (threadIdRef.current && threadActivities[threadIdRef.current] === 'completed') {
      updateThreadActivity(threadIdRef.current, null);
    }
  };

  const closeChat = (): void => {
    historyAbortRef.current?.abort();
    isOpenRef.current = false;
    setIsOpen(false);
    clearRecoveryPollTimer();

    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
    }

    closeTimerRef.current = window.setTimeout(() => {
      setIsPanelRendered(false);
      closeTimerRef.current = null;
    }, PANEL_ANIMATION_DURATION);
  };

  const startResize = (event: React.MouseEvent<HTMLDivElement>): void => {
    event.preventDefault();
    setIsResizing(true);
  };

  const resetCurrentChat = (): void => {
    historyAbortRef.current?.abort();
    historyAbortRef.current = null;
    foregroundRequestTokenRef.current = null;
    clearInterruptedThreadId();
    clearRecoveryPollTimer();
    threadIdRef.current = null;
    setThreadId(null);
    setMessages([]);
    setSuggestedActions([]);
    setDraft('');
    setStatus(EMPTY_STATUS);
    setRunProgress(null);
    setIsResponseStarted(false);
    setIsCancelling(false);
    setPending(false);
    setHistoryLoading(false);
    setRecoveringThreadId(null);
  };

  const startNewChat = (): void => {
    resetCurrentChat();
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    sendMessage().catch(handleSendError);
  };

  const getVisibleActiveRequest = (): IAiAssistantActiveRequest | null => {
    const foregroundRequestToken = foregroundRequestTokenRef.current;
    const foregroundRequest = foregroundRequestToken ? activeRequestsRef.current.get(foregroundRequestToken) : null;

    if (foregroundRequest && !foregroundRequest.finished) return foregroundRequest;

    return threadIdRef.current ? getLatestRequestForThread(threadIdRef.current, false) : null;
  };

  const requestActiveRunCancellation = async (request: IAiAssistantActiveRequest): Promise<void> => {
    if (request.finished || request.cancellationRequestSent || !request.runId) return;

    request.cancellationRequestSent = true;

    try {
      await cancelAiAssistantRun(request.runId, {
        orgOguid,
        userOguid,
        locale: language,
      });
    } catch (error) {
      request.cancellationRequested = false;
      request.cancellationRequestSent = false;

      if (request.finished) return;

      if (request.token === foregroundRequestTokenRef.current || isRequestVisible(request)) {
        setIsCancelling(false);
        handleHistoryError(error, t('common:aiAssistant.cancelError'));
      }
    }
  };

  const handleStop = (): void => {
    const request = getVisibleActiveRequest();

    if (!request || request.finished || request.cancellationRequested) return;

    request.cancellationRequested = true;
    setIsCancelling(true);
    requestActiveRunCancellation(request).catch(() => undefined);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key !== 'Enter' || event.shiftKey) return;

    event.preventDefault();
    sendMessage().catch(handleSendError);
  };

  const handleSuggestedActionClick = (action: IAiAssistantSuggestedAction): void => {
    if (action.kind === 'tool') {
      sendToolAction(action).catch(handleSendError);

      return;
    }

    if (action.prompt) {
      sendMessage(action.prompt).catch(handleSendError);

      return;
    }

    setStatus(t('common:aiAssistant.requestError'));
  };

  const handleThreadClick = (thread: IAiAssistantThread): void => {
    if (historyLoading) return;

    if (thread.id === threadId) {
      if (threadActivities[thread.id] === 'completed') updateThreadActivity(thread.id, null);

      return;
    }

    historyAbortRef.current?.abort();

    const controller = new AbortController();
    const cachedRequest = getLatestRequestForThread(thread.id);
    const runningRequest = getLatestRequestForThread(thread.id, false);

    historyAbortRef.current = controller;
    foregroundRequestTokenRef.current = runningRequest?.token || null;
    threadIdRef.current = thread.id;
    if (threadActivities[thread.id] === 'completed') updateThreadActivity(thread.id, null);
    setThreadId(thread.id);
    setMessages(mergeCachedRequestMessages([], cachedRequest));
    setSuggestedActions(cachedRequest?.suggestedActions || []);
    setDraft('');
    setStatus(EMPTY_STATUS);
    setRunProgress(runningRequest?.progress || null);
    setIsResponseStarted(runningRequest?.responseStarted || false);
    setIsCancelling(runningRequest?.cancellationRequested || false);
    setPending(!!runningRequest);
    setHistoryLoading(true);
    setRecoveringThreadId(null);

    loadThreadMessages(thread.id, controller.signal)
      .then((hasRecoveredResponse) => {
        if (runningRequest || getInterruptedThreadId() !== thread.id) return;

        if (hasRecoveredResponse) {
          finishInterruptedRecovery(thread.id);

          return;
        }

        setRecoveringThreadId(thread.id);
        scheduleInterruptedThreadRefresh(thread.id);
      })
      .catch((error) => handleHistoryError(error, t('common:aiAssistant.threadMessagesLoadError')))
      .finally(() => {
        if (historyAbortRef.current === controller) {
          historyAbortRef.current = null;
        }

        if (threadIdRef.current === thread.id) {
          setHistoryLoading(false);
        }
      });
  };

  const handleThreadDeleteClick = (event: ReactMouseEvent, thread: IAiAssistantThread): void => {
    event.preventDefault();
    event.stopPropagation();

    if (deletingThreadId) return;

    const threadTitle = thread.title || t('common:aiAssistant.untitledThread');

    if (!window.confirm(t('common:aiAssistant.deleteChatConfirm', { title: threadTitle }))) return;

    const controller = new AbortController();
    const isActiveThread = thread.id === threadIdRef.current || thread.id === threadId;

    setDeletingThreadId(thread.id);

    deleteAiAssistantThread(thread.id, {
      orgOguid,
      userOguid,
      locale: language,
      signal: controller.signal,
    })
      .then(() => {
        setThreads((currentThreads) => currentThreads.filter((currentThread) => currentThread.id !== thread.id));
        updateThreadActivity(thread.id, null);
        activeRequestsRef.current.forEach((request, token) => {
          if (request.threadId === thread.id) activeRequestsRef.current.delete(token);
        });
        clearInterruptedThreadId(thread.id);

        if (isActiveThread) {
          resetCurrentChat();
        }

        loadThreads(false).catch(() => undefined);
      })
      .catch((error) => handleHistoryError(error, t('common:aiAssistant.deleteChatError')))
      .finally(() => {
        setDeletingThreadId(null);
      });
  };

  const handleDocumentLinkClick = (event: ReactMouseEvent, path: string): void => {
    if (shouldUseBrowserNavigation(event)) return;

    event.preventDefault();
    event.stopPropagation();
    history.push(path);
  };

  const handleDocumentRowClick = (event: ReactMouseEvent, path: string): void => {
    if (shouldUseBrowserNavigation(event)) return;
    if (hasSelectedText()) return;

    history.push(path);
  };

  const handleDownloadClick = (download: IAiAssistantDownloadPart): void => {
    if (!orgOguid || !userOguid) {
      setStatus(t('common:aiAssistant.contextError'));

      return;
    }

    downloadAiAssistantFile(download.downloadPath, {
      orgOguid,
      userOguid,
      locale: language,
    })
      .then((file) => fileDownload(file, download.fileName, file.type))
      .catch((error) => handleHistoryError(error, t('common:aiAssistant.requestError')));
  };

  const handleSendError = (error: Error, requestToken?: string): void => {
    if (error.name === 'AbortError') {
      return;
    }

    const request = requestToken ? activeRequestsRef.current.get(requestToken) : null;

    if (!request || isRequestVisible(request)) {
      addAssistantError(error.message || t('common:aiAssistant.requestError'), undefined, requestToken);
      setStatus(t('common:error'));
    }

    if (request) finishRequest(request);
  };

  const sendMessage = async (messageText?: string): Promise<void> => {
    if (isRunActive || historyLoading || recoveringThreadId) return;

    const text = (messageText || draft).trim();

    if (!text) {
      setStatus(t('common:aiAssistant.enterMessage'));

      return;
    }

    await sendAssistantRequest({
      displayText: text,
      text,
    });
  };

  const sendToolAction = async (action: IAiAssistantSuggestedAction): Promise<void> => {
    if (isRunActive || historyLoading || recoveringThreadId) return;

    if (action.kind !== 'tool' || !action.toolName || !action.toolArguments) {
      setStatus(t('common:aiAssistant.requestError'));

      return;
    }

    await sendAssistantRequest({
      displayText: action.label,
      toolCall: {
        toolName: action.toolName,
        toolArguments: action.toolArguments,
      },
    });
  };

  const sendAssistantRequest = async ({ displayText, text, toolCall }: IAiAssistantSendRequest): Promise<void> => {
    if (!orgOguid || !userOguid) {
      setStatus(t('common:aiAssistant.contextError'));

      return;
    }

    const messageId = uuidv4();
    const correlationId = uuidv4();
    const controller = new AbortController();
    const requestToken = correlationId;
    const currentThreadId = threadIdRef.current || threadId;

    activeRequestsRef.current.forEach((request, token) => {
      if (request.finished && request.threadId === currentThreadId) activeRequestsRef.current.delete(token);
    });
    const activeRequest: IAiAssistantActiveRequest = {
      token: requestToken,
      controller,
      runId: null,
      threadId: currentThreadId,
      threadTitle: displayText,
      messageId: null,
      progressClosed: false,
      progress: null,
      responseStarted: false,
      userMessage: {
        id: messageId,
        role: 'user',
        text: displayText,
        status: 'sent',
      },
      assistantMessage: null,
      suggestedActions: [],
      cancellationRequested: false,
      cancellationRequestSent: false,
      finished: false,
    };
    activeRequestsRef.current.set(requestToken, activeRequest);
    foregroundRequestTokenRef.current = requestToken;
    if (currentThreadId) updateThreadActivity(currentThreadId, 'running');
    setPending(true);
    setRunProgress(null);
    setIsResponseStarted(false);
    setIsCancelling(false);
    setDraft('');
    setStatus(EMPTY_STATUS);
    setSuggestedActions([]);
    setMessages((current) => upsertAssistantMessageInList(current, activeRequest.userMessage));

    try {
      const streamParams = {
        threadId: currentThreadId,
        orgOguid,
        userOguid,
        messageId,
        correlationId,
        signal: controller.signal,
        locale: language,
        timezone: getUserTimeZone(),
        onEvent: (event: IAiAssistantStreamEvent) => applyEvent(event, requestToken),
      };

      if (toolCall) {
        await streamAiAssistantChat({
          ...streamParams,
          toolCall,
        });
      } else {
        await streamAiAssistantChat({
          ...streamParams,
          text: text || '',
        });
      }

      completeStreamingMessages(requestToken);
      if (isRequestVisible(activeRequest)) setStatus(EMPTY_STATUS);
    } catch (error) {
      handleSendError(error, requestToken);
    } finally {
      if (!activeRequest.finished) finishRequest(activeRequest);
    }
  };

  const applyEvent = ({ event, data }: IAiAssistantStreamEvent, requestToken: string): void => {
    const activeRequest = activeRequestsRef.current.get(requestToken);

    if (!activeRequest || activeRequest.finished) return;

    if (event === 'run.created') {
      updateActiveRequestIdentifiers(activeRequest, data);
      if (activeRequest.threadId) updateThreadActivity(activeRequest.threadId, 'running');
      if (activeRequest.cancellationRequested) requestActiveRunCancellation(activeRequest).catch(() => undefined);

      return;
    }

    if (event === 'run.progress') {
      applyRunProgress(data, requestToken);

      return;
    }

    if (event === 'thread.created') {
      const createdThreadId = getThreadId(data);

      if (createdThreadId) {
        activeRequest.threadId = createdThreadId;
        activeRequest.threadTitle = getStringField(data, 'title') || activeRequest.threadTitle;
        updateThreadActivity(createdThreadId, 'running');
        setThreads((current) => upsertAssistantThread(current, createdThreadId, activeRequest.threadTitle));

        if (foregroundRequestTokenRef.current === requestToken) {
          threadIdRef.current = createdThreadId;
          setThreadId(createdThreadId);
        }
      }

      return;
    }

    if (event === 'suggested_actions.updated') {
      activeRequest.suggestedActions = extractSuggestedActions(data);
      if (isRequestVisible(activeRequest)) setSuggestedActions(activeRequest.suggestedActions);

      return;
    }

    const messageId = getMessageId(data);

    if (event === 'error') {
      closeRunProgress(data, requestToken);
      addAssistantError(getErrorText(data, t('common:aiAssistant.requestError')), messageId, requestToken);
      if (isRequestVisible(activeRequest)) setStatus(t('common:error'));
      finishRequest(activeRequest);

      return;
    }

    switch (event) {
      case 'tool.started':
      case 'tool.completed':
        return;

      case 'response.started':
        closeRunProgress(data, requestToken);

        return;

      case 'run.completed':
      case 'run.timed_out':
        closeRunProgress(data, requestToken);
        finishRequest(activeRequest);

        return;

      case 'run.cancelled':
        closeRunProgress(data, requestToken);
        if (isRequestVisible(activeRequest)) setStatus(t('common:aiAssistant.cancelled'));
        finishRequest(activeRequest);

        return;

      case 'message.started':
        activeRequest.suggestedActions = [];
        if (isRequestVisible(activeRequest)) setSuggestedActions([]);

        return;

      case 'message.part': {
        closeRunProgress(data, requestToken);
        const download = extractDownloadPart(data);

        if (download) {
          appendAssistantDownload(messageId, download, requestToken);

          return;
        }

        const table = extractTablePart(data);

        if (table) {
          appendAssistantTable(messageId, table, requestToken);
        }

        return;
      }

      case 'message.completed': {
        closeRunProgress(data, requestToken);
        const text = extractText(data);
        const hasParts = hasMessageParts(data);
        const tables = hasParts ? extractTables(data) : [];
        const downloads = extractDownloads(data);
        const shouldReplaceParts = hasParts || downloads.length > 0;
        const completedSuggestedActions = extractSuggestedActions(data);

        if (hasSuggestedActions(data)) {
          activeRequest.suggestedActions = completedSuggestedActions;
          if (isRequestVisible(activeRequest)) setSuggestedActions(completedSuggestedActions);
        }

        completeAssistantMessage(requestToken, messageId, text, tables, downloads, shouldReplaceParts);

        return;
      }

      case 'message.delta':
        closeRunProgress(data, requestToken);
        appendAssistantText(messageId, extractText(data), requestToken);
    }
  };

  const applyRunProgress = (data: unknown, requestToken: string): void => {
    const progress = normalizeRunProgress(data);
    const activeRequest = activeRequestsRef.current.get(requestToken);

    if (!progress || !activeRequest || activeRequest.finished || activeRequest.progressClosed) return;
    if (activeRequest.runId && activeRequest.runId !== progress.runId) return;
    if (activeRequest.threadId && activeRequest.threadId !== progress.threadId) return;
    if (activeRequest.messageId && activeRequest.messageId !== progress.messageId) return;

    activeRequest.runId = progress.runId;
    activeRequest.threadId = progress.threadId;
    activeRequest.messageId = progress.messageId;
    if (activeRequest.progress?.runId === progress.runId && activeRequest.progress.sequence >= progress.sequence) return;

    activeRequest.progress = progress;
    updateThreadActivity(progress.threadId, 'running');
    if (activeRequest.cancellationRequested) requestActiveRunCancellation(activeRequest).catch(() => undefined);

    if (isRequestVisible(activeRequest)) setRunProgress(progress);
  };

  const closeRunProgress = (data: unknown, requestToken: string): void => {
    const activeRequest = activeRequestsRef.current.get(requestToken);

    if (!activeRequest || activeRequest.finished) return;

    const eventRunId = getStringField(data, 'runId');
    const eventThreadId = getStringField(data, 'threadId');
    const eventMessageId = getStringField(data, 'messageId');

    if (eventRunId && activeRequest.runId && eventRunId !== activeRequest.runId) return;
    if (eventThreadId && activeRequest.threadId && eventThreadId !== activeRequest.threadId) return;
    if (eventMessageId && activeRequest.messageId && eventMessageId !== activeRequest.messageId) return;

    updateActiveRequestIdentifiers(activeRequest, data);
    activeRequest.progressClosed = true;
    activeRequest.responseStarted = true;
    activeRequest.progress = null;

    if (isRequestVisible(activeRequest)) {
      setIsResponseStarted(true);
      setRunProgress(null);
    }
  };

  const finishRequest = (request: IAiAssistantActiveRequest): void => {
    if (request.finished) return;

    const wasActivelyViewed = isOpenRef.current && isRequestVisible(request);

    request.finished = true;
    request.progressClosed = true;
    request.responseStarted = true;
    request.progress = null;
    request.cancellationRequested = false;
    request.cancellationRequestSent = false;

    if (request.threadId) {
      updateThreadActivity(request.threadId, wasActivelyViewed ? null : 'completed');
      clearInterruptedThreadId(request.threadId);
    }

    if (isRequestVisible(request)) {
      setRunProgress(null);
      setPending(false);
      setIsResponseStarted(false);
      setIsCancelling(false);
    }

    loadThreads(false).catch(() => undefined);
  };

  const updateRequestAssistantMessage = (
    requestToken: string,
    update: (current: IAiAssistantMessage | null) => IAiAssistantMessage,
  ): void => {
    const request = activeRequestsRef.current.get(requestToken);

    if (!request) return;

    const nextMessage = update(request.assistantMessage);

    request.assistantMessage = nextMessage;

    if (isRequestVisible(request)) {
      setMessages((current) => upsertAssistantMessageInList(current, nextMessage));
    }
  };

  const appendAssistantText = (id: string, text: string, requestToken: string): void => {
    if (!text) return;

    updateRequestAssistantMessage(requestToken, (current) => ({
      id,
      role: 'assistant',
      text: `${current?.text || ''}${text}`,
      tables: current?.tables,
      downloads: current?.downloads,
      status: 'streaming',
    }));
  };

  const appendAssistantTable = (id: string, table: IAiAssistantTablePart, requestToken: string): void => {
    updateRequestAssistantMessage(requestToken, (current) => ({
      id,
      role: 'assistant',
      text: current?.text || '',
      tables: [...(current?.tables || []), table],
      downloads: current?.downloads,
      status: 'streaming',
    }));
  };

  const appendAssistantDownload = (id: string, download: IAiAssistantDownloadPart, requestToken: string): void => {
    updateRequestAssistantMessage(requestToken, (current) => ({
      id,
      role: 'assistant',
      text: current?.text || '',
      tables: current?.tables,
      downloads: [...(current?.downloads || []), download],
      status: 'streaming',
    }));
  };

  const completeAssistantMessage = (
    requestToken: string,
    id: string,
    text: string,
    tables: IAiAssistantTablePart[],
    downloads: IAiAssistantDownloadPart[],
    shouldReplaceParts: boolean,
  ): void => {
    updateRequestAssistantMessage(requestToken, (current) => ({
      id,
      role: 'assistant',
      text: current?.text || text,
      tables: shouldReplaceParts ? tables : current?.tables,
      downloads: shouldReplaceParts ? downloads : current?.downloads,
      status: 'completed',
    }));
  };

  const completeStreamingMessages = (requestToken: string): void => {
    const request = activeRequestsRef.current.get(requestToken);

    if (!request?.assistantMessage || request.assistantMessage.status !== 'streaming') return;

    const completedMessage: IAiAssistantMessage = hasAssistantContent(request.assistantMessage)
      ? { ...request.assistantMessage, status: 'completed' }
      : { ...request.assistantMessage, text: t('common:aiAssistant.emptyResponse'), status: 'error' };

    request.assistantMessage = completedMessage;

    if (isRequestVisible(request)) {
      setMessages((current) => upsertAssistantMessageInList(current, completedMessage));
    }
  };

  const addAssistantError = (text: string, id = uuidv4(), requestToken?: string): void => {
    const errorMessage: IAiAssistantMessage = {
      id,
      role: 'assistant',
      text,
      status: 'error',
    };
    const request = requestToken ? activeRequestsRef.current.get(requestToken) : null;

    if (request) request.assistantMessage = errorMessage;
    if (request && !isRequestVisible(request)) return;

    setMessages((current) => upsertAssistantMessageInList(current, errorMessage));
  };

  const renderLoader = (): ReactElement => (
    <div className={styles.loader} aria-label={t('common:aiAssistant.answering')}>
      <span />
      <span />
      <span />
    </div>
  );

  const renderMessageContent = (message: IAiAssistantMessage): ReactNode => {
    const tables = message.tables || [];
    const downloads = message.downloads || [];

    if (message.role === 'assistant' && message.status === 'streaming' && !hasAssistantContent(message)) {
      return renderLoader();
    }

    return (
      <>
        {message.text && (
          <div className={message.role === 'assistant' ? styles.markdown : undefined}>
            {message.role === 'assistant' ? renderMarkdownText(message.text) : renderPlainTextWithLinks(message.text, message.id)}
          </div>
        )}
        {tables.map((table, index) => renderTable(table, `${message.id}-${index}`))}
        {downloads.map((download, index) => renderDownload(download, `${message.id}-${index}`))}
      </>
    );
  };

  const renderDownload = (download: IAiAssistantDownloadPart, key: string): ReactElement => {
    const shouldFetchDownload = shouldFetchAiAssistantDownload(download.downloadPath);

    return (
      <div className={styles.downloadPart} key={key}>
        {shouldFetchDownload ? (
          <button className={styles.downloadLink} onClick={() => handleDownloadClick(download)} type='button'>
            {download.fileName}
          </button>
        ) : (
          <a
            className={styles.downloadLink}
            download={download.fileName}
            href={getAiAssistantDownloadUrl(download.downloadPath)}
            rel='noopener noreferrer'
            target='_blank'
          >
            {download.fileName}
          </a>
        )}
        {typeof download.rowCount === 'number' && <div className={styles.downloadMeta}>rowCount: {download.rowCount}</div>}
      </div>
    );
  };

  const renderTable = (table: IAiAssistantTablePart, key: string): ReactElement => (
    <div className={styles.tablePart} key={key}>
      {table.title && <div className={styles.tableTitle}>{table.title}</div>}
      <div className={styles.tableScroller}>
        <table className={styles.table}>
          <thead>
            <tr>
              {getVisibleTableColumns(table).map((column) => (
                <th key={column.key}>{column.label || column.key}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, rowIndex) => {
              const documentPath = getTableRowDocumentPath(table, row, orgAlias);
              const documentHref = documentPath ? getAbsoluteDocumentHref(documentPath) : null;
              const rowTooltip = getTableRowTooltip(table, row);

              return (
                <tr
                  className={documentPath ? styles.tableRowLink : undefined}
                  key={rowIndex}
                  onClick={documentPath ? (event) => handleDocumentRowClick(event, documentPath) : undefined}
                  title={rowTooltip}
                >
                  {getVisibleTableColumns(table).map((column) => {
                    const cellText = formatTableCell(row[column.key]);

                    return (
                      <td key={column.key}>
                        {documentHref && documentPath ? (
                          <a
                            className={styles.tableCellLink}
                            href={documentHref}
                            onClick={(event) => handleDocumentLinkClick(event, documentPath)}
                          >
                            {cellText}
                          </a>
                        ) : (
                          cellText
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  const shouldShowWaitingIndicator = (): boolean =>
    ((isRunActive && !isResponseStarted) || historyLoading || !!recoveringThreadId) &&
    !messages.some((message) => message.role === 'assistant' && message.status === 'streaming');

  const getRunProgressText = (): string => {
    if (!runProgress) return '';

    switch (runProgress.phase) {
      case 'QUEUED':
        return t('common:aiAssistant.progress.queued');
      case 'AGENT_PREPARING':
        return t('common:aiAssistant.progress.agentPreparing');
      case 'MODEL_PROCESSING':
        return t('common:aiAssistant.progress.modelProcessing');
      case 'AGENT_PROCESSING':
        return t('common:aiAssistant.progress.agentProcessing');
      case 'SQL_EXECUTING':
        return t('common:aiAssistant.progress.sqlExecuting');
      case 'TOOL_EXECUTING':
        return t('common:aiAssistant.progress.toolExecuting');
      case 'EXPORT_EXECUTING':
        return t('common:aiAssistant.progress.exportExecuting');
      case 'FINALIZING':
        return t('common:aiAssistant.progress.finalizing');
      default:
        return t('common:aiAssistant.progress.fallback');
    }
  };

  const shouldShowInitialState = (): boolean => messages.length === 0 && !isRunActive && !historyLoading && !recoveringThreadId;

  const shouldShowSuggestedActions = (): boolean =>
    suggestedActions.length > 0 && !isRunActive && !historyLoading && !recoveringThreadId;

  const getActionButtonText = (): string => {
    if (!isRunActive) return t('common:send');

    return isCancelling ? t('common:aiAssistant.stopping') : t('common:aiAssistant.stop');
  };

  const handlePanelAnimationEnd = (event: React.AnimationEvent<HTMLElement>): void => {
    if (event.currentTarget !== event.target || isOpen) return;

    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }

    setIsPanelRendered(false);
  };

  const renderSuggestedActions = (actions: IAiAssistantSuggestedAction[]): ReactElement => (
    <div className={styles.suggestedActions}>
      {actions.map((action) => (
        <div className={styles.suggestedAction} key={action.id}>
          <button className={styles.suggestedActionButton} onClick={() => handleSuggestedActionClick(action)} type='button'>
            {action.label}
          </button>
          {action.description && <span className={styles.suggestedActionDescription}>{action.description}</span>}
        </div>
      ))}
    </div>
  );

  const renderThreads = (): ReactElement => (
    <section aria-label={t('common:aiAssistant.history')} className={styles.threadsPanel}>
      <div className={styles.threadsHeader}>
        <span>{t('common:aiAssistant.history')}</span>
        {threadsLoading && <span className={styles.threadsLoading}>{t('common:aiAssistant.historyLoading')}</span>}
      </div>

      <div className={styles.threadList}>
        {!threadsLoading && threads.length === 0 && <div className={styles.threadEmpty}>{t('common:aiAssistant.historyEmpty')}</div>}

        {threads.map((thread) => {
          const threadDate = formatThreadDate(thread.updatedAt || thread.createdAt);
          const isDeleting = deletingThreadId === thread.id;
          const activity = threadActivities[thread.id];
          const activityClass = activity === 'running' ? styles.threadButtonRunning : activity === 'completed' ? styles.threadButtonCompleted : '';

          return (
            <div
              className={`${styles.threadButton} ${thread.id === threadId ? styles.threadButtonActive : ''} ${activityClass}`}
              key={thread.id}
              title={thread.title || t('common:aiAssistant.untitledThread')}
            >
              <button
                className={styles.threadSelectButton}
                disabled={isDeleting || (historyLoading && thread.id === threadId)}
                onClick={() => handleThreadClick(thread)}
                type='button'
              >
                <span className={styles.threadTitle}>{thread.title || t('common:aiAssistant.untitledThread')}</span>
                <span className={styles.threadMeta}>
                  {threadDate && <span className={styles.threadDate}>{threadDate}</span>}
                  {activity === 'running' && <span className={styles.threadRunning}>{t('common:aiAssistant.threadRunning')}</span>}
                  {activity === 'completed' && <span className={styles.threadCompleted}>{t('common:aiAssistant.threadCompleted')}</span>}
                </span>
              </button>
              <button
                aria-label={t('common:aiAssistant.deleteChat')}
                className={styles.threadDeleteButton}
                disabled={isDeleting || activity === 'running'}
                onClick={(event) => handleThreadDeleteClick(event, thread)}
                title={t('common:aiAssistant.deleteChat')}
                type='button'
              >
                ×
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );

  const getInitialSuggestedActions = (): IAiAssistantSuggestedAction[] => [
    {
      id: 'show_documents',
      label: t('common:aiAssistant.initialActions.showDocuments.label'),
      description: t('common:aiAssistant.initialActions.showDocuments.description'),
      prompt: t('common:aiAssistant.initialActions.showDocuments.prompt'),
      kind: 'prompt',
    },
    {
      id: 'show_contractors',
      label: t('common:aiAssistant.initialActions.showContractors.label'),
      description: t('common:aiAssistant.initialActions.showContractors.description'),
      prompt: t('common:aiAssistant.initialActions.showContractors.prompt'),
      kind: 'prompt',
    },
    {
      id: 'analyze_amounts_dates_statuses',
      label: t('common:aiAssistant.initialActions.analyzeAmountsDatesStatuses.label'),
      description: t('common:aiAssistant.initialActions.analyzeAmountsDatesStatuses.description'),
      prompt: t('common:aiAssistant.initialActions.analyzeAmountsDatesStatuses.prompt'),
      kind: 'prompt',
    },
  ];

  const renderInitialState = (): ReactElement => (
    <>
      <div className={`${styles.message} ${styles.messageAssistant}`}>
        <div className={styles.bubble}>{t('common:aiAssistant.welcomeMessage')}</div>
      </div>

      {renderSuggestedActions(getInitialSuggestedActions())}
    </>
  );

  const renderMessages = (): ReactElement => (
    <div className={styles.messages} ref={messagesRef}>
      {shouldShowInitialState() && renderInitialState()}

      {messages.map((message) => (
        <div className={`${styles.message} ${message.role === 'user' ? styles.messageUser : styles.messageAssistant}`} key={message.id}>
          <div className={`${styles.bubble} ${message.status === 'error' ? styles.error : ''}`}>{renderMessageContent(message)}</div>
        </div>
      ))}

      {shouldShowWaitingIndicator() && (
        <div aria-live='polite' className={`${styles.message} ${styles.messageAssistant} ${styles.waitingIndicator}`}>
          <div className={`${styles.bubble} ${styles.loaderBubble}`}>{renderLoader()}</div>
          {runProgress && <span className={styles.progressText}>{getRunProgressText()}</span>}
        </div>
      )}

      {shouldShowSuggestedActions() && renderSuggestedActions(suggestedActions)}
    </div>
  );

  return (
    <>
      {!isPanelRendered && (
        <div className={styles.floatingLauncher}>
          <button
            aria-label={t('common:aiAssistant.open')}
            className={styles.floatingLauncherButton}
            onClick={openChat}
            title={t('common:aiAssistant.open')}
            type='button'
          >
            <div aria-hidden='true' className={styles.floatingRevealHandle}>
              <div className={styles.floatingRevealBadge}>
                <svg className={styles.floatingRevealBadge__icon} viewBox='0 0 24 24'>
                  <path d='M9.2 1.5C10 6.4 11.6 8 16.5 8.8C11.6 9.6 10 11.2 9.2 16.1C8.4 11.2 6.8 9.6 1.9 8.8C6.8 8 8.4 6.4 9.2 1.5Z' />
                  <path d='M16.5 10.9C17 14.2 18.3 15.5 21.6 16C18.3 16.5 17 17.8 16.5 21.1C16 17.8 14.7 16.5 11.4 16C14.7 15.5 16 14.2 16.5 10.9Z' />
                </svg>
              </div>
            </div>
            <span className={styles.floatingButton}>
              <IconMessageFill externalClass={styles.floatingButton__icon} />
            </span>
          </button>
        </div>
      )}

      {isPanelRendered && (
        <aside
          aria-label={t('common:aiAssistant.title')}
          className={`${styles.panel} ${isOpen ? styles.panelOpen : styles.panelClose}`}
          onAnimationEnd={handlePanelAnimationEnd}
          style={{ width: panelWidth }}
        >
          <div
            aria-label={t('common:aiAssistant.resize')}
            className={styles.resizeHandle}
            onMouseDown={startResize}
            role='separator'
          />
          <header className={styles.header}>
            <h2 className={styles.title}>{t('common:aiAssistant.title')}</h2>
            <button className={styles.newChatButton} onClick={startNewChat} type='button'>
              {t('common:aiAssistant.newChat')}
            </button>
            <button aria-label={t('common:aiAssistant.close')} className={styles.closeButton} onClick={closeChat} type='button'>
              ×
            </button>
          </header>

          {renderThreads()}

          {renderMessages()}

          <form className={styles.form} onSubmit={handleSubmit}>
            <textarea
              className={styles.textarea}
              disabled={isRunActive || historyLoading || !!recoveringThreadId}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('common:aiAssistant.messagePlaceholder')}
              value={draft}
            />
            <div className={styles.footer}>
              <div className={styles.status}>{status}</div>
              <button
                className={`${styles.sendButton} ${isRunActive ? styles.stopButton : ''}`}
                disabled={isRunActive ? isCancelling : historyLoading || !!recoveringThreadId || !draft.trim()}
                onClick={isRunActive ? handleStop : undefined}
                type={isRunActive ? 'button' : 'submit'}
              >
                {getActionButtonText()}
              </button>
            </div>
          </form>
        </aside>
      )}
    </>
  );
};

const upsertAssistantThread = (threads: IAiAssistantThread[], id: string, title: string): IAiAssistantThread[] => {
  const existingThread = threads.find((thread) => thread.id === id);

  if (existingThread) {
    return threads.map((thread) => (thread.id === id && !thread.title && title ? { ...thread, title } : thread));
  }

  return [
    {
      id,
      title,
    },
    ...threads,
  ];
};

const mergeRequestThreads = (
  threads: IAiAssistantThread[],
  requests: Map<string, IAiAssistantActiveRequest>,
): IAiAssistantThread[] =>
  Array.from(requests.values()).reduce(
    (result, request) =>
      request.threadId
        ? upsertAssistantThread(result, request.threadId, request.threadTitle)
        : result,
    threads,
  );

const upsertAssistantMessageInList = (
  messages: IAiAssistantMessage[],
  nextMessage: IAiAssistantMessage,
): IAiAssistantMessage[] => {
  const exists = messages.some((message) => message.id === nextMessage.id);

  return exists
    ? messages.map((message) => (message.id === nextMessage.id ? nextMessage : message))
    : [...messages, nextMessage];
};

const mergeCachedRequestMessages = (
  messages: IAiAssistantMessage[],
  request: IAiAssistantActiveRequest | null,
): IAiAssistantMessage[] => {
  if (!request) return messages;

  let mergedMessages = upsertAssistantMessageInList(messages, request.userMessage);
  const cachedAssistantMessage = request.assistantMessage;

  if (!cachedAssistantMessage) return mergedMessages;

  const storedMessage = mergedMessages.find((message) => message.id === cachedAssistantMessage.id);

  if (storedMessage?.status === 'completed' && cachedAssistantMessage.status !== 'completed') return mergedMessages;

  mergedMessages = upsertAssistantMessageInList(mergedMessages, cachedAssistantMessage);

  return mergedMessages;
};

const normalizeRunProgress = (data: unknown): IAiAssistantRunProgress | null => {
  if (!isRecord(data)) return null;

  const runId = getStringField(data, 'runId');
  const threadId = getStringField(data, 'threadId');
  const messageId = getStringField(data, 'messageId');
  const phase = getStringField(data, 'phase');
  const sequence = data.sequence;

  if (!runId || !threadId || !messageId || !phase || typeof sequence !== 'number' || !Number.isFinite(sequence) || sequence < 1) {
    return null;
  }

  return {
    runId,
    threadId,
    messageId,
    sequence,
    phase,
    activityKind: getStringField(data, 'activityKind') || undefined,
    toolCallId: getStringField(data, 'toolCallId') || undefined,
    toolName: getStringField(data, 'toolName') || undefined,
  };
};

const updateActiveRequestIdentifiers = (activeRequest: IAiAssistantActiveRequest, data: unknown): void => {
  const runId = getStringField(data, 'runId');
  const threadId = getStringField(data, 'threadId');
  const messageId = getStringField(data, 'messageId');

  if (runId) activeRequest.runId = runId;
  if (threadId) activeRequest.threadId = threadId;
  if (messageId) activeRequest.messageId = messageId;
};

const getStringField = (data: unknown, key: string): string => {
  if (!isRecord(data) || typeof data[key] !== 'string') return '';

  return data[key] as string;
};

const getMessageId = (data: unknown): string => {
  if (isRecord(data) && typeof data.messageId === 'string') {
    return data.messageId;
  }

  return ASSISTANT_FALLBACK_MESSAGE_ID;
};

const getThreadId = (data: unknown): string => {
  if (isRecord(data) && typeof data.threadId === 'string') {
    return data.threadId;
  }

  return '';
};

const getErrorText = (data: unknown, fallback: string): string => {
  if (isRecord(data)) {
    if (typeof data.message === 'string') return data.message;
    if (typeof data.code === 'string') return data.code;
  }

  if (typeof data === 'string') return data;

  return fallback;
};

const extractText = (data: unknown): string => {
  if (typeof data === 'string') return data;
  if (!isRecord(data)) return '';

  if (typeof data.delta === 'string') return data.delta;
  if (typeof data.text === 'string') return data.text;
  if (typeof data.content === 'string') return data.content;

  if (isRecord(data.delta)) {
    if (typeof data.delta.text === 'string') return data.delta.text;
    if (typeof data.delta.content === 'string') return data.delta.content;
  }

  if (Array.isArray(data.parts)) return extractTextFromParts(data.parts);

  if (isRecord(data.message) && Array.isArray(data.message.parts)) {
    return extractTextFromParts(data.message.parts);
  }

  return '';
};

const extractSuggestedActions = (data: unknown): IAiAssistantSuggestedAction[] => {
  if (!isRecord(data)) return [];

  let items: unknown[] = [];

  if (Array.isArray(data.items)) {
    items = data.items;
  } else if (Array.isArray(data.suggestedActions)) {
    items = data.suggestedActions;
  }

  return normalizeSuggestedActions(items);
};

const normalizeSuggestedActions = (items: unknown[]): IAiAssistantSuggestedAction[] =>
  items.reduce<IAiAssistantSuggestedAction[]>((actions, item) => {
    if (!isRecord(item)) return actions;
    if (typeof item.label !== 'string') return actions;

    const id = typeof item.id === 'string' ? item.id : `${item.kind || 'action'}-${actions.length}-${item.label}`;
    const description = typeof item.description === 'string' ? item.description : '';

    if (item.kind === 'prompt') {
      if (typeof item.prompt !== 'string') return actions;

      return [
        ...actions,
        {
          id,
          label: item.label,
          description,
          prompt: item.prompt,
          kind: 'prompt',
        },
      ];
    }

    if (item.kind === 'tool') {
      if (!isSupportedToolAction(item.toolName) || !isRecord(item.toolArguments)) return actions;

      return [
        ...actions,
        {
          id,
          label: item.label,
          description,
          kind: 'tool',
          toolName: item.toolName,
          toolArguments: item.toolArguments,
        },
      ];
    }

    return actions;
  }, []);

const isSupportedToolAction = (toolName: unknown): toolName is AiAssistantToolName =>
  typeof toolName === 'string' && SUPPORTED_TOOL_ACTIONS.includes(toolName as AiAssistantToolName);

const hasSuggestedActions = (data: unknown): boolean =>
  isRecord(data) && (Array.isArray(data.items) || Array.isArray(data.suggestedActions));

const extractTablePart = (data: unknown): IAiAssistantTablePart | null => {
  if (!isRecord(data)) return null;

  return normalizeTablePart(data.part);
};

const extractDownloadPart = (data: unknown): IAiAssistantDownloadPart | null => {
  if (!isRecord(data)) return null;

  return normalizeDownloadPart(data.part) || normalizeDownloadPart(data);
};

const extractTables = (data: unknown): IAiAssistantTablePart[] => {
  const parts = getMessageParts(data);

  return parts.reduce<IAiAssistantTablePart[]>((tables, part) => {
    const table = normalizeTablePart(part);

    return table ? [...tables, table] : tables;
  }, []);
};

const extractDownloads = (data: unknown): IAiAssistantDownloadPart[] => {
  const parts = getMessageParts(data);
  const partDownloads = parts.reduce<IAiAssistantDownloadPart[]>((downloads, part) => {
    const download = normalizeDownloadPart(part);

    return download ? [...downloads, download] : downloads;
  }, []);
  const directDownload = normalizeDownloadPart(data);

  return directDownload ? [...partDownloads, directDownload] : partDownloads;
};

const normalizeHistoryMessages = (historyMessages: IAiAssistantHistoryMessage[]): IAiAssistantMessage[] =>
  historyMessages.map((message) => {
    const partsData = { parts: message.parts };
    const tables = message.role === 'assistant' ? extractTables(partsData) : [];
    const downloads = message.role === 'assistant' ? extractDownloads(partsData) : [];
    const partsText = extractText(partsData);
    const hasDisplayableParts = !!partsText || tables.length > 0 || downloads.length > 0;
    const text = message.role === 'assistant' && hasDisplayableParts ? partsText : message.text || partsText;

    return {
      id: message.id,
      role: message.role,
      text,
      tables,
      downloads,
      status: message.role === 'user' ? 'sent' : 'completed',
    };
  });

const getLatestSuggestedActions = (historyMessages: IAiAssistantHistoryMessage[]): IAiAssistantSuggestedAction[] => {
  const latestMessage = historyMessages[historyMessages.length - 1];

  return latestMessage?.role === 'assistant' ? normalizeSuggestedActions(latestMessage.suggestedActions) : [];
};

const hasAssistantResponseAfterLastUser = (messages: IAiAssistantMessage[]): boolean => {
  const lastUserIndex = findLastMessageIndex(messages, (message) => message.role === 'user');
  const messagesAfterLastUser = lastUserIndex >= 0 ? messages.slice(lastUserIndex + 1) : messages;

  return messagesAfterLastUser.some((message) => message.role === 'assistant' && hasAssistantContent(message));
};

const findLastMessageIndex = (
  messages: IAiAssistantMessage[],
  predicate: (message: IAiAssistantMessage) => boolean,
): number => {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (predicate(messages[index])) {
      return index;
    }
  }

  return -1;
};

const getMessageParts = (data: unknown): unknown[] => {
  if (!isRecord(data)) return [];
  if (Array.isArray(data.parts)) return data.parts;
  if (isRecord(data.message) && Array.isArray(data.message.parts)) return data.message.parts;

  return [];
};

const hasMessageParts = (data: unknown): boolean =>
  isRecord(data) && (Array.isArray(data.parts) || (isRecord(data.message) && Array.isArray(data.message.parts)));

const normalizeTablePart = (part: unknown): IAiAssistantTablePart | null => {
  if (isExportResult(part)) return null;
  if (isTechnicalPart(part)) return null;

  if (!isRecord(part) || part.type !== 'table' || !Array.isArray(part.columns) || !Array.isArray(part.rows)) {
    return null;
  }

  const columns = part.columns.reduce<IAiAssistantTablePart['columns']>((result, column) => {
    if (!isRecord(column) || typeof column.key !== 'string') return result;
    if (isHiddenTableColumnKey(column.key)) return result;

    return [
      ...result,
      {
        key: column.key,
        label: typeof column.label === 'string' ? column.label : column.key,
        type: typeof column.type === 'string' ? column.type : undefined,
      },
    ];
  }, []);

  if (!columns.length) return null;

  const rows = part.rows.filter(isRecord);

  return {
    type: 'table',
    title: typeof part.title === 'string' ? part.title : undefined,
    columns,
    rows,
    rowCount: typeof part.rowCount === 'number' ? part.rowCount : undefined,
    truncated: typeof part.truncated === 'boolean' ? part.truncated : undefined,
    metadata: isRecord(part.metadata) ? part.metadata : undefined,
  };
};

const normalizeDownloadPart = (part: unknown): IAiAssistantDownloadPart | null => {
  if (isTechnicalPart(part)) return null;

  const payload = getDownloadPayload(part);

  if (!payload || typeof payload.downloadPath !== 'string') return null;

  return {
    type: 'download',
    fileName: typeof payload.fileName === 'string' ? payload.fileName : 'export.xlsx',
    downloadPath: payload.downloadPath,
    rowCount: typeof payload.rowCount === 'number' ? payload.rowCount : undefined,
    exportId: typeof payload.exportId === 'string' ? payload.exportId : undefined,
    toolCallId: getPayloadString(payload, 'toolCallId'),
    mcpToolCallId: getPayloadString(payload, 'mcpToolCallId'),
  };
};

const getPayloadString = (payload: Record<string, unknown>, key: string): string | undefined => {
  const value = payload[key];

  if (typeof value === 'string') return value;

  const metadataValue = isRecord(payload.metadata) ? payload.metadata[key] : undefined;

  if (typeof metadataValue === 'string') return metadataValue;

  return undefined;
};

const getDownloadPayload = (value: unknown): Record<string, unknown> | null => {
  if (!isRecord(value)) return null;
  if (typeof value.downloadPath === 'string') return value;

  const nestedValues = [value.result, value.data, value.content, value.output];

  for (const nestedValue of nestedValues) {
    if (isRecord(nestedValue) && typeof nestedValue.downloadPath === 'string') {
      return nestedValue;
    }

    const parsedValue = parseJsonRecord(nestedValue);

    if (parsedValue && typeof parsedValue.downloadPath === 'string') {
      return parsedValue;
    }
  }

  return null;
};

const isExportResult = (value: unknown): boolean => {
  if (!isRecord(value)) return false;

  if (getDownloadPayload(value)) return true;
  if (value.toolName === 'export_excel') return true;
  if (isRecord(value.metadata) && value.metadata.toolName === 'export_excel') return true;
  if (isRecord(value.result) && value.result.toolName === 'export_excel') return true;

  return false;
};

const isTechnicalPart = (value: unknown): boolean => {
  if (!isRecord(value) || !isRecord(value.metadata)) return false;

  return value.metadata.resultPurpose === 'technical';
};

const hasAssistantContent = (message: IAiAssistantMessage): boolean =>
  !!message.text.trim() || !!message.tables?.length || !!message.downloads?.length;

const parseJsonRecord = (value: unknown): Record<string, unknown> | null => {
  if (typeof value !== 'string') return null;

  try {
    const parsedValue = JSON.parse(value);

    return isRecord(parsedValue) ? parsedValue : null;
  } catch {
    return null;
  }
};

const formatTableCell = (value: unknown): string => {
  if (value === null || typeof value === 'undefined') return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const getVisibleTableColumns = (table: IAiAssistantTablePart): IAiAssistantTablePart['columns'] =>
  table.columns.filter((column) => !isHiddenTableColumnKey(column.key));

const getTableRowTooltip = (table: IAiAssistantTablePart, row: Record<string, unknown>): string =>
  getVisibleTableColumns(table)
    .map((column) => `${column.label || column.key}: ${formatTableCell(row[column.key])}`)
    .join('\n');

const isHiddenTableColumnKey = (key: string): boolean => HIDDEN_TABLE_COLUMN_KEYS.includes(key as (typeof HIDDEN_TABLE_COLUMN_KEYS)[number]);

const getTableRowDocumentPath = (table: IAiAssistantTablePart, row: Record<string, unknown>, orgAlias: string): string | null => {
  if (!orgAlias) return null;

  const actionDocumentOguid = getOpenDocumentCardDocumentOguid(row);

  if (actionDocumentOguid) return `/${orgAlias}/documents/${actionDocumentOguid}`;

  const documentIdColumn = getDocumentIdColumn(table);

  if (!documentIdColumn) return null;

  const documentId = getDocumentId(row[documentIdColumn.key]);

  return documentId ? `/${orgAlias}/documents/${documentId}` : null;
};

const getDocumentIdColumn = (table: IAiAssistantTablePart): IAiAssistantTablePart['columns'][number] | null =>
  table.columns.find(isDocumentIdColumn) || null;

const isDocumentIdColumn = (column: IAiAssistantTablePart['columns'][number]): boolean => {
  if (DOCUMENT_ID_COLUMN_KEYS.includes(column.key as (typeof DOCUMENT_ID_COLUMN_KEYS)[number])) return true;

  const label = (column.label || column.key).trim().toLowerCase();

  return DOCUMENT_ID_COLUMN_LABELS.includes(label as (typeof DOCUMENT_ID_COLUMN_LABELS)[number]);
};

const getOpenDocumentCardDocumentOguid = (row: Record<string, unknown>): string | null =>
  getDocumentOguidFromRowAction(row.rowAction) || getDocumentOguidFromRowAction(row.action);

const getDocumentOguidFromRowAction = (action: unknown): string | null => {
  if (!isRecord(action) || action.type !== OPEN_DOCUMENT_CARD_ACTION_TYPE) return null;

  const documentOguid = getDocumentId(action.documentOguid);

  if (documentOguid) return documentOguid;

  if (!isRecord(action.payload)) return null;

  return getDocumentId(action.payload.documentOguid);
};

const getDocumentId = (value: unknown): string | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);

  if (typeof value === 'string') {
    const trimmedValue = value.trim();

    return trimmedValue ? trimmedValue : null;
  }

  return null;
};

const getAbsoluteDocumentHref = (path: string): string => {
  if (typeof window === 'undefined') return path;

  return `${window.location.origin}${path}`;
};

const formatThreadDate = (value?: string): string => {
  if (!value) return '';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return '';

  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })}`;
};

const shouldUseBrowserNavigation = (event: ReactMouseEvent): boolean =>
  event.defaultPrevented || event.button !== 0 || event.metaKey || event.altKey || event.ctrlKey || event.shiftKey;

const hasSelectedText = (): boolean => {
  if (typeof window === 'undefined') return false;

  return !!window.getSelection()?.toString();
};

const shouldFetchAiAssistantDownload = (downloadPath: string): boolean => {
  if (!/^https?:\/\//.test(downloadPath)) return true;

  try {
    return new URL(downloadPath).pathname.includes('/assistant/');
  } catch {
    return false;
  }
};

const renderMarkdownText = (text: string, keyPrefix = 'md'): ReactNode[] => {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const nodes: ReactNode[] = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];

    if (!line.trim()) {
      index += 1;

      continue;
    }

    if (line.trim().startsWith('```')) {
      const codeLines: string[] = [];

      index += 1;

      while (index < lines.length && !lines[index].trim().startsWith('```')) {
        codeLines.push(lines[index]);
        index += 1;
      }

      if (index < lines.length) {
        index += 1;
      }

      nodes.push(
        <pre className={styles.markdownCodeBlock} key={`${keyPrefix}-code-${nodes.length}`}>
          <code>{codeLines.join('\n')}</code>
        </pre>,
      );

      continue;
    }

    if (/^\s*>\s?/.test(line)) {
      const quoteLines: string[] = [];

      while (index < lines.length && /^\s*>\s?/.test(lines[index])) {
        quoteLines.push(lines[index].replace(/^\s*>\s?/, ''));
        index += 1;
      }

      nodes.push(
        <blockquote className={styles.markdownBlockquote} key={`${keyPrefix}-quote-${nodes.length}`}>
          {renderMarkdownText(quoteLines.join('\n'), `${keyPrefix}-quote-${nodes.length}`)}
        </blockquote>,
      );

      continue;
    }

    if (/^\s*[-*]\s+/.test(line) || /^\s*\d+\.\s+/.test(line)) {
      const isOrdered = /^\s*\d+\.\s+/.test(line);
      const items: string[] = [];

      while (index < lines.length && (isOrdered ? /^\s*\d+\.\s+/.test(lines[index]) : /^\s*[-*]\s+/.test(lines[index]))) {
        items.push(lines[index].replace(isOrdered ? /^\s*\d+\.\s+/ : /^\s*[-*]\s+/, ''));
        index += 1;
      }

      const ListTag = isOrdered ? 'ol' : 'ul';

      nodes.push(
        <ListTag className={styles.markdownList} key={`${keyPrefix}-list-${nodes.length}`}>
          {items.map((item, itemIndex) => (
            <li key={`${keyPrefix}-list-${nodes.length}-${itemIndex}`}>{renderInlineMarkdown(item, `${keyPrefix}-li-${nodes.length}-${itemIndex}`)}</li>
          ))}
        </ListTag>,
      );

      continue;
    }

    const paragraphLines: string[] = [];

    while (
      index < lines.length &&
      lines[index].trim() &&
      !lines[index].trim().startsWith('```') &&
      !/^\s*>\s?/.test(lines[index]) &&
      !/^\s*[-*]\s+/.test(lines[index]) &&
      !/^\s*\d+\.\s+/.test(lines[index])
    ) {
      paragraphLines.push(lines[index]);
      index += 1;
    }

    nodes.push(
      <p className={styles.markdownParagraph} key={`${keyPrefix}-p-${nodes.length}`}>
        {renderInlineMarkdown(paragraphLines.join('\n'), `${keyPrefix}-p-${nodes.length}`)}
      </p>,
    );
  }

  return nodes;
};

const renderInlineMarkdown = (text: string, keyPrefix: string): ReactNode[] => {
  const nodes: ReactNode[] = [];
  let cursor = 0;
  let plainText = '';

  const flushPlainText = (): void => {
    if (!plainText) return;

    nodes.push(...renderPlainTextWithLinks(plainText, `${keyPrefix}-text-${nodes.length}`));
    plainText = '';
  };

  while (cursor < text.length) {
    const codeEnd = text[cursor] === '`' ? text.indexOf('`', cursor + 1) : -1;

    if (codeEnd > cursor + 1) {
      flushPlainText();
      nodes.push(
        <code className={styles.markdownInlineCode} key={`${keyPrefix}-code-${nodes.length}`}>
          {text.slice(cursor + 1, codeEnd)}
        </code>,
      );
      cursor = codeEnd + 1;

      continue;
    }

    const linkNode = parseMarkdownLink(text, cursor, `${keyPrefix}-link-${nodes.length}`);

    if (linkNode) {
      flushPlainText();
      nodes.push(linkNode.node);
      cursor = linkNode.endIndex;

      continue;
    }

    const decoratedNode = parseDecoratedInlineMarkdown(text, cursor, `${keyPrefix}-decor-${nodes.length}`);

    if (decoratedNode) {
      flushPlainText();
      nodes.push(decoratedNode.node);
      cursor = decoratedNode.endIndex;

      continue;
    }

    plainText += text[cursor];
    cursor += 1;
  }

  flushPlainText();

  return nodes;
};

const parseMarkdownLink = (text: string, startIndex: number, key: string): { node: ReactNode; endIndex: number } | null => {
  if (text[startIndex] !== '[') return null;

  const labelEnd = text.indexOf(']', startIndex + 1);

  if (labelEnd <= startIndex + 1 || text[labelEnd + 1] !== '(') return null;

  const hrefEnd = text.indexOf(')', labelEnd + 2);

  if (hrefEnd <= labelEnd + 2) return null;

  const href = text.slice(labelEnd + 2, hrefEnd).trim();

  if (!isSafeMarkdownHref(href)) return null;

  return {
    node: (
      <a className={styles.link} href={href} key={key} rel='noopener noreferrer' target='_blank'>
        {renderInlineMarkdown(text.slice(startIndex + 1, labelEnd), `${key}-label`)}
      </a>
    ),
    endIndex: hrefEnd + 1,
  };
};

const parseDecoratedInlineMarkdown = (text: string, startIndex: number, key: string): { node: ReactNode; endIndex: number } | null => {
  const decorations = [
    { delimiter: '**', tag: 'strong' as const },
    { delimiter: '__', tag: 'strong' as const },
    { delimiter: '~~', tag: 's' as const },
    { delimiter: '*', tag: 'em' as const },
    { delimiter: '_', tag: 'em' as const },
  ];

  for (const { delimiter, tag: Tag } of decorations) {
    if (!text.startsWith(delimiter, startIndex)) continue;

    const contentStart = startIndex + delimiter.length;
    const contentEnd = text.indexOf(delimiter, contentStart);

    if (contentEnd <= contentStart) continue;
    if (delimiter.length === 1 && !isSingleDecorationBoundary(text, startIndex, contentEnd)) continue;

    return {
      node: <Tag key={key}>{renderInlineMarkdown(text.slice(contentStart, contentEnd), `${key}-${Tag}`)}</Tag>,
      endIndex: contentEnd + delimiter.length,
    };
  }

  return null;
};

const isSingleDecorationBoundary = (text: string, startIndex: number, endIndex: number): boolean => {
  const previousChar = text[startIndex - 1] || '';
  const firstContentChar = text[startIndex + 1] || '';
  const lastContentChar = text[endIndex - 1] || '';
  const nextChar = text[endIndex + 1] || '';

  return !isAlphaNumeric(previousChar) && !/\s/.test(firstContentChar) && !/\s/.test(lastContentChar) && !isAlphaNumeric(nextChar);
};

const isAlphaNumeric = (value: string): boolean => /^[a-zа-яё0-9]$/i.test(value);

const renderPlainTextWithLinks = (text: string, keyPrefix: string): ReactNode[] =>
  text.split(URL_REGEXP).map((part, index) => {
    if (!part.match(URL_REGEXP)) return part;

    const href = part.replace(/[),.;:!?]+$/, '');
    const suffix = part.slice(href.length);

    return (
      <React.Fragment key={`${keyPrefix}-${index}`}>
        <a className={styles.link} href={href} rel='noopener noreferrer' target='_blank'>
          {href}
        </a>
        {suffix}
      </React.Fragment>
    );
  });

const isSafeMarkdownHref = (href: string): boolean => /^https?:\/\//i.test(href);

const extractTextFromParts = (parts: unknown[]): string =>
  parts
    .map((part) => {
      if (isTechnicalPart(part)) return '';

      if (isRecord(part) && part.type === 'text' && typeof part.text === 'string') {
        return part.text;
      }

      return '';
    })
    .join('');

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

export default withRouter(AiAssistant);
